/*======================================================================================================================
  * Bluebell: src/app/bramble/template-cases/template-case.service.ts
  * Last Edited: 26/06/2025
  * Copyright 2024-2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { Injectable, inject } from '@angular/core';
import { RestService } from '@app/services/rest.service';
import { environment } from '@environments/environment';
import { selectAccountFeatures } from '@state/account/account.selectors';
import { refreshAccessToken } from '@state/authentication/authentication.actions';
import { templateCaseActions } from '@state/template-cases/template-case.actions';
import {
  TemplateCase,
  TemplateFeature,
  SelectedTab,
} from '@state/template-cases/template-case.interfaces';
import { Urls } from '@state/urls';
import { Subject } from 'rxjs';
import { IFile } from '@app/components/file-input/file-select.component';
import { Store } from '@ngrx/store';

@Injectable({
    providedIn: 'root'
})
export class TemplateCaseService {
    private rest = inject(RestService);
    private store = inject(Store);

    clonedCaseSubject$ = new Subject<TemplateCase>();
    accountFeatures = this.store.selectSignal(selectAccountFeatures);

    upload(
      programme: string,
      name: string,
      description: string,
      methodology_group: string,
      workflows: string[],
      file: File,
      retry = true
    ) {
        const xhr = new XMLHttpRequest();
        const url: string = environment.SERVER + Urls.templateCases;
        const form = new FormData();
        workflows = workflows ? workflows : [];
        form.append('programme', programme);
        form.append('name', name);
        form.append('description', description);
        form.append('methodology_group', methodology_group);
        form.append('workflows', JSON.stringify(workflows));
        form.append('file', file);

        xhr.onreadystatechange = () => {
            // When complete
            if (xhr.readyState === 4) {

                if (xhr.status === 200) {
                    // If successful
                    const updatedTemplateCase: TemplateCase = JSON.parse(xhr.response);
                    this.store.dispatch(templateCaseActions.uploadSuccess(updatedTemplateCase));
                } else if (xhr.status === 401 && retry) {
                    // If unsuccessful and unauthorized, try again but don't retry if that attempt fails
                    this.store.dispatch(refreshAccessToken());
                    this.upload(programme, name, description, methodology_group, workflows, file, false);
                } else {
                    const error = JSON.parse(xhr.response).detail;
                    this.store.dispatch(templateCaseActions.uploadFailure({ templateCaseName: name, error }));
                }
            }
        };

        xhr.open('POST', url, true);
        Object.entries(this.rest.baseHeaders).forEach(([key, value]) => xhr.setRequestHeader(key, value));
        xhr.send(form);
    }

    replaceFile(templateCase: TemplateCase, file: IFile, retry = true): void {
        const url = `${environment.SERVER}${Urls.templateCases}${templateCase.uuid}/replace/`;
        const xhr = new XMLHttpRequest();
        const form = new FormData();
        form.append('file', file);

        xhr.onreadystatechange = () => {
            // When complete
            if (xhr.readyState === 4) {

                if (xhr.status === 200) {
                    // If successful
                    const updatedTemplateCase: TemplateCase = JSON.parse(xhr.response);
                    this.store.dispatch(templateCaseActions.replaceFileSuccess(updatedTemplateCase));
                } else if (xhr.status === 401 && retry) {
                    // If unsuccessful and unauthorized, try again but don't retry if that attempt fails
                    this.store.dispatch(refreshAccessToken());
                    this.replaceFile(templateCase, file, false);
                } else {
                    // If otherwise unsuccessful
                    const templateCaseName = templateCase.name;
                    const error = JSON.parse(xhr.response).detail;
                    this.store.dispatch(templateCaseActions.replaceFileFailure({ templateCaseName, error }));
                }
            }
        };

        xhr.open('POST', url, true);
        Object.entries(this.rest.baseHeaders).forEach(([key, value]) => xhr.setRequestHeader(key, value));
        xhr.send(form);
    }

    filterTabsByAccountSettings(selectedTabs: SelectedTab[]): SelectedTab[] {
      const filters: string[] = [];

      if (!this.accountFeatures()?.f1_wake_plots && selectedTabs.find(tab => tab.key === 'wake_grids')) {
        filters.push('wake_grids');
      }

      return selectedTabs.filter(tab => !filters.find(a => a === tab.key));
    }

    filterFeaturesByAccountSettings(selectedFeatures: TemplateFeature[]): TemplateFeature[] {
      const filters: string[] = [];

      this.store.select(selectAccountFeatures).subscribe(features => {
        if (features) {
          if (!features.allow_mapped_inlets && selectedFeatures.find(feature => feature.key === 'mapped_inlet')) {
            filters.push('mapped_inlet','export_mapped_inlet');
          }
        }
      });

      return selectedFeatures.filter(feature => !filters.find(a => a === feature.key));
    }
}
